import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, MessageCircle, Lock, Bookmark, BookmarkCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Shared match card used on /matches and /shortlist.
 * Renders match percentage, name, profession, weighted breakdown,
 * commonalities, and Report/Chat CTAs — plus a bookmark toggle.
 */
export default function MatchCard({ match, index = 0, isFree = false, onShortlistChange }) {
  const nav = useNavigate();
  const [shortlisted, setShortlisted] = useState(!!match.shortlisted);
  const [busy, setBusy] = useState(false);

  const toggleShortlist = async () => {
    if (busy) return;
    setBusy(true);
    // Optimistic
    const next = !shortlisted;
    setShortlisted(next);
    try {
      const { data } = await api.post("/shortlist/toggle", { candidate_id: match.candidate_id });
      setShortlisted(data.shortlisted);
      toast.success(data.shortlisted ? `${match.name} shortlisted` : `Removed from shortlist`);
      if (onShortlistChange) onShortlistChange(match.candidate_id, data.shortlisted);
    } catch {
      // Revert
      setShortlisted(!next);
      toast.error("Failed to update shortlist");
    } finally {
      setBusy(false);
    }
  };

  const pm = match.profile_match;
  const isKycVerified = match.kyc_status === "verified";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
      data-testid={`match-card-${index}`}
      className="border border-stone-200 rounded-2xl bg-white p-6 flex flex-col relative"
    >
      {/* Bookmark toggle — top-right corner. Larger tap target on mobile. */}
      <button
        type="button"
        onClick={toggleShortlist}
        aria-label={shortlisted ? "Remove from shortlist" : "Add to shortlist"}
        data-testid={`shortlist-toggle-${index}`}
        disabled={busy}
        className={`absolute top-4 right-4 w-11 h-11 sm:w-9 sm:h-9 rounded-full grid place-items-center transition-all border ${
          shortlisted
            ? "bg-amber-500 border-amber-500 text-white shadow-sm hover:bg-amber-600"
            : "bg-white border-stone-200 text-stone-500 hover:border-amber-400 hover:text-amber-700"
        } ${busy ? "opacity-60 cursor-wait" : ""}`}
      >
        {shortlisted ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
      </button>

      <div className="flex items-start justify-between pr-14 sm:pr-12">
        <div>
          <div className="overline">Match Percentage</div>
          <div className="font-serif-display text-4xl sm:text-4xl text-[#1E1B4B] mt-1">{match.overall_score}%</div>
          {pm && (
            <div className="text-[10px] text-stone-500 uppercase tracking-widest mt-1">
              Lifestyle 60% · Professional 40%
            </div>
          )}
        </div>
      </div>
      {/* Verified pill — ONLY shown when candidate.kyc_status === "verified" */}
      {isKycVerified && (
        <div
          data-testid={`match-verified-badge-${index}`}
          className="text-xs text-amber-700 uppercase tracking-widest bg-amber-50 border border-amber-200 rounded-full px-3 py-1 inline-flex items-center gap-1 self-start mt-3"
        >
          <ShieldCheck size={12} /> Verified
        </div>
      )}

      <h3 className="font-serif-display text-2xl text-[#1E1B4B] mt-5">{match.name}</h3>
      <div className="text-sm text-stone-600 mt-1">
        {match.profile.profession || match.profile.job_title} · {match.profile.city}
      </div>
      <div className="text-xs text-stone-500 mt-2">
        {match.profile.industry ? `${match.profile.industry} · ` : ""}
        {match.profile.education}
      </div>
      <p className="text-sm text-stone-600 mt-4 line-clamp-3">{match.profile.about_me}</p>

      {pm && (
        <div className="mt-5 space-y-2.5" data-testid={`match-breakdown-${index}`}>
          <div>
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-stone-600 flex items-center gap-1">
                Lifestyle &amp; Values <span className="text-[10px] text-amber-700">· 60%</span>
              </span>
              <span className="text-[#1E1B4B] font-medium">{pm.lifestyle_values_score}%</span>
            </div>
            <div className="mt-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500" style={{ width: `${pm.lifestyle_values_score}%` }} />
            </div>
          </div>
          <div>
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-stone-600 flex items-center gap-1">
                Professional &amp; Educational <span className="text-[10px] text-amber-700">· 40%</span>
              </span>
              <span className="text-[#1E1B4B] font-medium">{pm.professional_educational_score}%</span>
            </div>
            <div className="mt-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#1E1B4B]" style={{ width: `${pm.professional_educational_score}%` }} />
            </div>
          </div>
          {pm.commonalities?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {pm.commonalities.slice(0, 3).map((c) => (
                <span
                  key={c}
                  className="text-[10px] uppercase tracking-widest bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-2 py-0.5"
                >
                  ✓ {c}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button
          data-testid={`view-report-btn-${index}`}
          onClick={() => nav(`/matches/${match.candidate_id}/report`)}
          className={`btn-pill flex-1 min-h-[44px] text-sm flex items-center justify-center gap-1.5 py-3 sm:py-2 ${
            isFree ? "border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" : "text-white"
          }`}
          style={isFree ? {} : { background: "#1E1B4B" }}
        >
          {isFree ? <Lock size={14} /> : <FileText size={14} />}
          {isFree ? "Unlock Report" : "Full Report"}
        </button>
        <button
          data-testid={`chat-btn-${index}`}
          onClick={() => nav(`/chat/${match.candidate_id}`)}
          className="btn-pill flex-1 min-h-[44px] border border-stone-300 text-[#1E1B4B] text-sm flex items-center justify-center gap-1.5 hover:bg-stone-50 py-3 sm:py-2"
        >
          <MessageCircle size={14} /> Chat
        </button>
      </div>
    </motion.div>
  );
}
